import { useEffect, useReducer, useRef, useState } from "react";

import { apiClient } from "../../app/api/client";
import {
  displayUnitLabel,
  useUnitDisplay,
} from "../../app/units/UnitDisplayContext";
import { catalogApi } from "../catalog/api";

import {
  GoogleNetworkMapAdapter,
  loadGoogleMapsScript,
} from "./googleMapsAdapter";
import {
  createTopologyHistory,
  topologyHistoryReducer,
} from "./topologyHistory";

import type { CatalogApi } from "../catalog/api";
import type { HoseTypeRead } from "../catalog/types";
import type {
  CanonicalPoint,
  ProjectDocument,
  ProjectValidationIssue,
  TopologyCommand,
} from "./types";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { networkFieldHelp } from "../../app/guidance/fieldHelpRegistry";

export interface NetworkEditorProps {
  catalog?: Pick<CatalogApi, "listHoses">;
  document: ProjectDocument;
  onCommand?: (
    command: TopologyCommand,
    document: ProjectDocument,
  ) => Promise<ProjectDocument> | ProjectDocument;
  onDocumentChange?: (document: ProjectDocument) => void;
  validationIssues?: ProjectValidationIssue[];
}

function parsePath(value: string): CanonicalPoint[] | null {
  const lines = value
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean);
  const points: CanonicalPoint[] = [];
  for (const line of lines) {
    const [longitudeText, latitudeText, ...extra] = line
      .split(",")
      .map((part) => part.trim());
    const longitude = Number(longitudeText);
    const latitude = Number(latitudeText);
    if (
      extra.length > 0 ||
      !Number.isFinite(longitude) ||
      !Number.isFinite(latitude) ||
      longitude < -180 ||
      longitude > 180 ||
      latitude < -90 ||
      latitude > 90
    ) {
      return null;
    }
    points.push({ longitude_deg: longitude, latitude_deg: latitude });
  }
  return points;
}

function caughtMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The topology edit failed.";
}
function conciseDisplayNumber(value: number): string {
  return Number(value.toFixed(3)).toString();
}

export function NetworkEditor({
  catalog = catalogApi,
  document,
  onCommand,
  onDocumentChange,
  validationIssues,
}: NetworkEditorProps) {
  const { formatSiValue, toDisplayValue, toSiValue, units } = useUnitDisplay();
  const [history, dispatch] = useReducer(
    topologyHistoryReducer,
    document,
    createTopologyHistory,
  );
  const [selectedLinkId, setSelectedLinkId] = useState(
    document.links[0]?.id ?? "",
  );
  const [secondLinkId, setSecondLinkId] = useState("");
  const [targetNodeId, setTargetNodeId] = useState(document.nodes[0]?.id ?? "");
  const [selectedNodeId, setSelectedNodeId] = useState(
    document.nodes[0]?.id ?? "",
  );
  const [selectedNodeRole, setSelectedNodeRole] = useState<
    "junction" | "sink" | "source"
  >(document.nodes[0]?.node_type ?? "junction");
  const [siteAnchorKind, setSiteAnchorKind] = useState<"link" | "node">("node");
  const [siteCapacity, setSiteCapacity] = useState(1);
  const [siteSeries, setSiteSeries] = useState(true);
  const [siteParallel, setSiteParallel] = useState(true);
  const [chainageM, setChainageM] = useState(1);
  const [snapToleranceM, setSnapToleranceM] = useState(5);
  const [drawName, setDrawName] = useState("New topology");
  const [drawLongitude, setDrawLongitude] = useState("");
  const [drawLatitude, setDrawLatitude] = useState("");
  const [drawPath, setDrawPath] = useState("");
  const [drawStartNodeId, setDrawStartNodeId] = useState(
    document.nodes[0]?.id ?? "",
  );
  const [drawEndNodeId, setDrawEndNodeId] = useState(
    document.nodes[1]?.id ?? "",
  );
  const [hoseTypeId, setHoseTypeId] = useState("");
  const [hoses, setHoses] = useState<HoseTypeRead[]>([]);
  const [hoseError, setHoseError] = useState<string | null>(null);
  const [editPath, setEditPath] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const mapElement = useRef<HTMLDivElement | null>(null);
  const mapAdapter = useRef<GoogleNetworkMapAdapter | null>(null);
  const latestDocument = useRef(history.present);

  useEffect(() => {
    dispatch({ document, type: "reset" });
  }, [document]);

  useEffect(() => {
    let current = true;
    void catalog.listHoses("").then(
      (page) => {
        if (!current) return;
        setHoses(page.items);
        setHoseTypeId((value) => value || page.items[0]?.id || "");
        setHoseError(null);
      },
      (caught: unknown) => {
        if (current) setHoseError(caughtMessage(caught));
      },
    );
    return () => {
      current = false;
    };
  }, [catalog]);

  useEffect(() => {
    const link = history.present.links.find(
      (item) => item.id === selectedLinkId,
    );
    setEditPath(
      link?.path
        .map((point) => `${point.longitude_deg}, ${point.latitude_deg}`)
        .join("\n") ?? "",
    );
  }, [history.present.links, selectedLinkId]);

  useEffect(() => {
    latestDocument.current = history.present;
    mapAdapter.current?.renderProject(history.present);
  }, [history.present]);

  useEffect(
    () => () => {
      mapAdapter.current?.dispose();
      mapAdapter.current = null;
    },
    [],
  );

  const execute = async (command: TopologyCommand) => {
    if (onCommand === undefined) {
      setError("No feature-local topology command handler is registered.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await onCommand(command, history.present);
      dispatch({ document: updated, type: "commit" });
      onDocumentChange?.(updated);
    } catch (caught) {
      setError(caughtMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const loadMap = async () => {
    setBusy(true);
    setError(null);
    try {
      const { api_key: mapsKey } = await apiClient.get<{ api_key: string }>(
        "/settings/google-maps/browser-key",
        { session: true },
      );
      await loadGoogleMapsScript(mapsKey);
      if (mapElement.current === null) {
        throw new Error("The network map container is unavailable.");
      }
      const currentDocument = latestDocument.current;
      const enteredLongitude = Number(drawLongitude);
      const enteredLatitude = Number(drawLatitude);
      const savedCenter = currentDocument.nodes[0]?.location;
      const enteredCenter =
        drawLongitude.trim() !== "" &&
        drawLatitude.trim() !== "" &&
        Number.isFinite(enteredLongitude) &&
        enteredLongitude >= -180 &&
        enteredLongitude <= 180 &&
        Number.isFinite(enteredLatitude) &&
        enteredLatitude >= -90 &&
        enteredLatitude <= 90
          ? {
              latitude_deg: enteredLatitude,
              longitude_deg: enteredLongitude,
            }
          : null;
      const center = savedCenter ??
        enteredCenter ?? { latitude_deg: 39.5, longitude_deg: -98.35 };
      mapAdapter.current?.dispose();
      mapAdapter.current = null;
      setMapReady(false);
      const adapter = new GoogleNetworkMapAdapter(mapElement.current, center, {
        onMapClick: (point) => {
          setDrawLongitude(point.longitude_deg.toString());
          setDrawLatitude(point.latitude_deg.toString());
          setStatus(
            `Coordinates: ${point.longitude_deg}, ${point.latitude_deg}.`,
          );
        },
        zoom: savedCenter === undefined && enteredCenter === null ? 4 : 13,
      });
      adapter.renderProject(currentDocument);
      mapAdapter.current = adapter;
      setMapReady(true);
      setStatus("Map loaded.");
    } catch (caught) {
      setError(caughtMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const undo = () => {
    const previous = history.past.at(-1);
    dispatch({ type: "undo" });
    if (previous !== undefined) {
      onDocumentChange?.(previous);
    }
  };
  const redo = () => {
    const next = history.future[0];
    dispatch({ type: "redo" });
    if (next !== undefined) {
      onDocumentChange?.(next);
    }
  };

  return (
    <section aria-labelledby="network-editor-title" className="network-panel">
      <h2 id="network-editor-title">Route map</h2>
      <div className="network-map-key">
        <button disabled={busy} onClick={() => void loadMap()} type="button">
          Load map
        </button>
        <button disabled={busy || !mapReady} onClick={() => mapAdapter.current?.fitProject()} type="button">
          Fit route
        </button>
      </div>
      <details className="network-technical">
        <summary>Map legend</summary>
        <ul aria-label="Network map legend" className="network-map-legend">
          <li>Green: source / permitted area or route</li>
          <li>Blue: sink / hydraulic hose link</li>
          <li>Gray: junction / reference overlay</li>
          <li>Orange: approved pump site</li>
          <li>Red: exclusion area</li>
        </ul>
      </details>
      <div aria-label="Network map" className="network-map" ref={mapElement} />
      {status === null ? null : <p role="status">{status}</p>}
      {error === null ? null : <p role="alert">{error}</p>}
      <details className="network-technical">
        <summary>Technical rules</summary>
        <p>
          Geometry is WGS84 longitude/latitude. Edits invalidate terrain. Saved
          geometry remains available without the live map.
        </p>
      </details>

      <details className="network-toolbox">
        <summary>Add nodes, routes, or pump sites</summary>

        <fieldset disabled={busy}>
          <legend>Add network items</legend>
          <label>
            Name
            <input
              {...guided(networkFieldHelp.drawName)}
              aria-label="Drawn element name"
              onChange={(event) => setDrawName(event.currentTarget.value)}
              value={drawName}
            />
          </label>
          <label>
            Longitude (WGS84 decimal degrees)
            <input
              {...guided(networkFieldHelp.longitude)}
              aria-label="Draw longitude"
              inputMode="decimal"
              onChange={(event) => setDrawLongitude(event.currentTarget.value)}
              value={drawLongitude}
            />
          </label>
          <label>
            Latitude (WGS84 decimal degrees)
            <input
              {...guided(networkFieldHelp.latitude)}
              aria-label="Draw latitude"
              inputMode="decimal"
              onChange={(event) => setDrawLatitude(event.currentTarget.value)}
              value={drawLatitude}
            />
          </label>
          {(["source", "junction", "sink"] as const).map((geometry) => {
            const longitude = Number(drawLongitude);
            const latitude = Number(drawLatitude);
            const valid =
              drawName.trim() !== "" &&
              Number.isFinite(longitude) &&
              longitude >= -180 &&
              longitude <= 180 &&
              Number.isFinite(latitude) &&
              latitude >= -90 &&
              latitude <= 90;
            return (
              <button
                disabled={!valid}
                key={geometry}
                onClick={() =>
                  void execute({
                    geometry,
                    location: {
                      longitude_deg: longitude,
                      latitude_deg: latitude,
                    },
                    name: drawName,
                    type: "draw",
                  })
                }
                type="button"
              >
                Draw {geometry}
              </button>
            );
          })}
          <p>
            For paths, enter one <code>longitude, latitude</code> pair per line.
            Hose endpoints are pinned to the selected nodes.
          </p>
          <label>
            Path vertices
            <textarea
              {...guided(networkFieldHelp.drawPath)}
              aria-label="Draw path vertices"
              onChange={(event) => setDrawPath(event.currentTarget.value)}
              rows={5}
              value={drawPath}
            />
          </label>
          <label>
            Hose start node
            <select
              {...guided(networkFieldHelp.hoseStart)}
              aria-label="Hose start node"
              onChange={(event) =>
                setDrawStartNodeId(event.currentTarget.value)
              }
              value={drawStartNodeId}
            >
              <option value="">Select a node</option>
              {history.present.nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Hose end node
            <select
              {...guided(networkFieldHelp.hoseEnd)}
              aria-label="Hose end node"
              onChange={(event) => setDrawEndNodeId(event.currentTarget.value)}
              value={drawEndNodeId}
            >
              <option value="">Select a node</option>
              {history.present.nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Active hose type
            <select
              {...guided(networkFieldHelp.activeHoseType)}
              aria-label="Active hose type"
              onChange={(event) => setHoseTypeId(event.currentTarget.value)}
              value={hoseTypeId}
            >
              <option value="">Select a catalog hose</option>
              {hoses.map((hose) => (
                <option key={hose.id} value={hose.id}>
                  {hose.name} ·{" "}
                  {hose.manufacturer ?? "Unspecified manufacturer"} {hose.model}{" "}
                  · {formatSiValue(hose.inside_diameter_m, "diameter")} ID
                </option>
              ))}
            </select>
          </label>
          {hoseError === null ? null : (
            <p role="alert">Hose catalog unavailable: {hoseError}</p>
          )}
          <button
            disabled={
              drawName.trim() === "" ||
              drawStartNodeId === "" ||
              drawEndNodeId === "" ||
              drawStartNodeId === drawEndNodeId ||
              hoseTypeId === ""
            }
            onClick={() => {
              const parsed = parsePath(drawPath);
              void execute({
                endNodeId: drawEndNodeId,
                geometry: "hose_link",
                hoseTypeId,
                name: drawName,
                ...(parsed !== null && parsed.length >= 2
                  ? { path: parsed }
                  : {}),
                startNodeId: drawStartNodeId,
                type: "draw",
              });
            }}
            type="button"
          >
            Draw hose link
          </button>
          <fieldset>
            <legend>Approved optimizer pump site</legend>
            <p>
              Anchor to an existing node, or enter a WGS84 point near the
              selected link. Link anchors snap within the project tolerance and
              become a hydraulic node without moving the route.
            </p>
            <label>
              Site anchor
              <select
                {...guided(networkFieldHelp.siteAnchor)}
                aria-label="Pump site anchor kind"
                onChange={(event) =>
                  setSiteAnchorKind(
                    event.currentTarget.value as "link" | "node",
                  )
                }
                value={siteAnchorKind}
              >
                <option value="node">Selected node</option>
                <option value="link">
                  Selected primary link near entered coordinates
                </option>
              </select>
            </label>
            <label>
              Maximum physical units
              <input
                {...guided(networkFieldHelp.siteCapacity)}
                aria-label="Pump site capacity"
                min="1"
                max="100"
                onChange={(event) =>
                  setSiteCapacity(event.currentTarget.valueAsNumber)
                }
                type="number"
                value={siteCapacity}
              />
            </label>
            <label>
              <input
                {...guided(networkFieldHelp.allowSeries)}
                checked={siteSeries}
                onChange={(event) => setSiteSeries(event.currentTarget.checked)}
                type="checkbox"
              />
              Allow series arrangement
            </label>
            <label>
              <input
                {...guided(networkFieldHelp.allowParallel)}
                checked={siteParallel}
                onChange={(event) =>
                  setSiteParallel(event.currentTarget.checked)
                }
                type="checkbox"
              />
              Allow parallel arrangement
            </label>
            <button
              disabled={
                drawName.trim() === "" ||
                !Number.isInteger(siteCapacity) ||
                siteCapacity < 1 ||
                (!siteSeries && !siteParallel) ||
                (siteAnchorKind === "node"
                  ? selectedNodeId === ""
                  : selectedLinkId === "" ||
                    !Number.isFinite(Number(drawLongitude)) ||
                    !Number.isFinite(Number(drawLatitude)))
              }
              onClick={() => {
                const allowedArrangements = [
                  ...(siteSeries ? (["series"] as const) : []),
                  ...(siteParallel ? (["parallel"] as const) : []),
                ];
                void execute({
                  allowedArrangements,
                  ...(siteAnchorKind === "node"
                    ? { anchorNodeId: selectedNodeId }
                    : {
                        anchorLinkId: selectedLinkId,
                        location: {
                          latitude_deg: Number(drawLatitude),
                          longitude_deg: Number(drawLongitude),
                        },
                      }),
                  geometry: "approved_pump_site",
                  maximumPhysicalUnits: siteCapacity,
                  name: drawName,
                  type: "draw",
                });
              }}
              type="button"
            >
              Add approved pump site
            </button>
          </fieldset>
          <button
            disabled={
              drawName.trim() === "" || (parsePath(drawPath)?.length ?? 0) < 3
            }
            onClick={() => {
              const path = parsePath(drawPath);
              if (path !== null)
                void execute({
                  geometry: "permit_area",
                  name: drawName,
                  path,
                  type: "draw",
                });
            }}
            type="button"
          >
            Draw permit area
          </button>
        </fieldset>
      </details>

      <details className="network-toolbox">
        <summary>Edit nodes and routes</summary>

        <fieldset disabled={busy}>
          <legend>Edit network items</legend>
          <label>
            Network node
            <select
              {...guided(networkFieldHelp.selectedNode)}
              aria-label="Selected network node"
              onChange={(event) => {
                const nodeId = event.currentTarget.value;
                setSelectedNodeId(nodeId);
                const node = history.present.nodes.find(
                  (item) => item.id === nodeId,
                );
                if (node !== undefined) setSelectedNodeRole(node.node_type);
              }}
              value={selectedNodeId}
            >
              <option value="">Select a named node</option>
              {history.present.nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name} — {node.node_type}
                </option>
              ))}
            </select>
          </label>
          <label>
            Hydraulic node role
            <select
              {...guided(networkFieldHelp.nodeRole)}
              aria-label="Hydraulic node role"
              onChange={(event) =>
                setSelectedNodeRole(
                  event.currentTarget.value as "junction" | "sink" | "source",
                )
              }
              value={selectedNodeRole}
            >
              <option value="junction">Junction</option>
              <option value="source">Source / draw</option>
              <option value="sink">Sink / delivery</option>
            </select>
          </label>
          <button
            disabled={selectedNodeId === ""}
            onClick={() =>
              void execute({
                nodeId: selectedNodeId,
                nodeType: selectedNodeRole,
                type: "edit_node_role",
              })
            }
            type="button"
          >
            Save node role
          </button>
          <label>
            Primary link
            <select
              {...guided(networkFieldHelp.primaryLink)}
              aria-label="Primary link"
              onChange={(event) => setSelectedLinkId(event.currentTarget.value)}
              value={selectedLinkId}
            >
              <option value="">Select a link</option>
              {history.present.links.map((link) => (
                <option key={link.id} value={link.id}>
                  {link.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Second link
            <select
              {...guided(networkFieldHelp.secondLink)}
              aria-label="Second link"
              onChange={(event) => setSecondLinkId(event.currentTarget.value)}
              value={secondLinkId}
            >
              <option value="">Select a link</option>
              {history.present.links.map((link) => (
                <option key={link.id} value={link.id}>
                  {link.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Split chainage ({displayUnitLabel(units.head)})
            <input
              {...guided(networkFieldHelp.splitChainage)}
              min="0"
              onChange={(event) =>
                setChainageM(
                  toSiValue(event.currentTarget.valueAsNumber, "head"),
                )
              }
              step="0.001"
              type="number"
              value={conciseDisplayNumber(toDisplayValue(chainageM, "head"))}
            />
          </label>
          <label>
            Edited path vertices
            <textarea
              {...guided(networkFieldHelp.editPath)}
              aria-label="Edited path vertices"
              onChange={(event) => setEditPath(event.currentTarget.value)}
              rows={5}
              value={editPath}
            />
          </label>
          <button
            disabled={
              selectedLinkId === "" || (parsePath(editPath)?.length ?? 0) < 2
            }
            onClick={() => {
              const path = parsePath(editPath);
              if (path !== null)
                void execute({
                  linkId: selectedLinkId,
                  path,
                  type: "edit_link_path",
                });
            }}
            type="button"
          >
            Save edited path
          </button>
          <button
            disabled={selectedLinkId === "" || !Number.isFinite(chainageM)}
            onClick={() =>
              void execute({
                distanceFromStartM: chainageM,
                linkId: selectedLinkId,
                type: "split_link",
              })
            }
            type="button"
          >
            Split link
          </button>
          <button
            disabled={selectedLinkId === "" || secondLinkId === ""}
            onClick={() =>
              void execute({
                firstLinkId: selectedLinkId,
                secondLinkId,
                type: "join_links",
              })
            }
            type="button"
          >
            Join links
          </button>
          <button
            disabled={selectedLinkId === ""}
            onClick={() =>
              void execute({ linkId: selectedLinkId, type: "reverse_link" })
            }
            type="button"
          >
            Reverse link
          </button>
          <button
            disabled={selectedLinkId === ""}
            onClick={() =>
              void execute({
                elementId: selectedLinkId,
                type: "delete_element",
              })
            }
            type="button"
          >
            Delete selected link
          </button>
          <label>
            Target node
            <select
              {...guided(networkFieldHelp.targetNode)}
              aria-label="Target node"
              onChange={(event) => setTargetNodeId(event.currentTarget.value)}
              value={targetNodeId}
            >
              <option value="">Select a node</option>
              {history.present.nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Snap tolerance ({displayUnitLabel(units.head)})
            <input
              {...guided(networkFieldHelp.snapTolerance)}
              min={toDisplayValue(0.001, "head")}
              onChange={(event) =>
                setSnapToleranceM(
                  toSiValue(event.currentTarget.valueAsNumber, "head"),
                )
              }
              step="0.001"
              type="number"
              value={conciseDisplayNumber(
                toDisplayValue(snapToleranceM, "head"),
              )}
            />
          </label>
          <button
            disabled={selectedLinkId === "" || targetNodeId === ""}
            onClick={() =>
              void execute({
                endpoint: "start",
                linkId: selectedLinkId,
                targetNodeId,
                toleranceM: snapToleranceM,
                type: "snap_endpoint",
              })
            }
            type="button"
          >
            Snap start endpoint
          </button>
          <button
            disabled={selectedLinkId === "" || targetNodeId === ""}
            onClick={() =>
              void execute({
                endpoint: "end",
                linkId: selectedLinkId,
                targetNodeId,
                toleranceM: snapToleranceM,
                type: "snap_endpoint",
              })
            }
            type="button"
          >
            Snap end endpoint
          </button>
        </fieldset>

        {onCommand === undefined ? (
          <div>
            <button
              disabled={history.past.length === 0}
              onClick={undo}
              type="button"
            >
              Undo
            </button>
            <button
              disabled={history.future.length === 0}
              onClick={redo}
              type="button"
            >
              Redo
            </button>
          </div>
        ) : (
          <p role="note">
            Undo and redo are unavailable for persisted revisions. Apply an
            explicit reverse edit so every saved revision remains auditable.
          </p>
        )}
      </details>

      <details className="network-technical">
        <summary>Network details</summary>
        <p>Revision {history.present.revision}</p>
        <ul aria-label="Network nodes">
          {history.present.nodes.map((node) => (
            <li key={node.id}>
              {node.name}: {node.node_type}
            </li>
          ))}
        </ul>
        <ul aria-label="Approved pump sites">
          {(history.present.candidate_pump_sites ?? []).map((site) => (
            <li key={site.id}>
              {site.name}: up to {site.maximum_physical_units} {site.maximum_physical_units === 1 ? 'unit' : 'units'} ·{" "}
              {site.allowed_arrangements.join(" / ")}
              <button
                onClick={() =>
                  void execute({ elementId: site.id, type: "delete_element" })
                }
                type="button"
              >
                Delete site {site.name}
              </button>
            </li>
          ))}
        </ul>
        <ul>
          {history.present.links.map((link) => (
            <li key={link.id}>
              {link.name}: {formatSiValue(link.geodesic_length_m, "head")} ·
              terrain {link.terrain_state}
            </li>
          ))}
        </ul>
      </details>

      <h3>Checks</h3>
      {validationIssues === undefined ? (
        <p role="status">Checks unavailable.</p>
      ) : validationIssues.length === 0 ? (
        <p>Ready.</p>
      ) : (
        <ul>
          {validationIssues.map((issue, index) => (
            <li key={`${issue.code}-${issue.object_id ?? "project"}-${index}`}>
              {issue.severity}: {issue.message} Action: {issue.action}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
